import React from 'react';
import { Table as BootstrapTable, TableProps as BootstrapTableProps } from 'react-bootstrap';

// Each column can define how it renders, what data it accesses, and optional metadata
export interface ColumnDef<T> {
  header: React.ReactNode | ((col: ColumnDef<T>) => React.ReactNode);
  accessorKey: keyof T | string;
  cell?: (row: T, col: ColumnDef<T>) => React.ReactNode;
  footer?: React.ReactNode | ((col: ColumnDef<T>) => React.ReactNode);
  meta?: unknown;
}
export interface TableProps<T> extends Omit<BootstrapTableProps, 'data'> {
  data: T[];
  columns: ColumnDef<T>[];
  rowKey?: (row: T, idx: number) => React.Key;
  className?: string;
  emptyState?: React.ReactNode;
  columnVisibility?: Record<string, boolean>;
}

// If a row has an 'id', use it for a stable React key
function hasId(obj: unknown): obj is { id: React.Key } {
  return typeof obj === 'object' && obj !== null && 'id' in obj;
}

// Prefer user-supplied rowKey, then 'id', then fallback to index for React keys
function getRowKey<T>(row: T, idx: number, rowKey?: (row: T, idx: number) => React.Key): React.Key {
  if (rowKey) return rowKey(row, idx);
  if (hasId(row)) {
    return row.id;
  }
  return idx;
}

export function Table<T>({
  data, columns, rowKey, className, emptyState, columnVisibility, ...rest
}: TableProps<T>) {
  const {
    striped, hover, size, variant, responsive,
  } = rest;
  if (data.length === 0) {
    return (
      <div>
        {emptyState ?? <span>Please provide a empty state</span>}
      </div>
    );
  }

  const visibleColumns = columns.filter((col) => {
    const columnVisibilityConfig = columnVisibility ?? {};
    const accessorKey = String(col?.accessorKey);
    const isKeyPresentInVisibility = accessorKey in columnVisibilityConfig;
    if (!isKeyPresentInVisibility) return true;
    return isKeyPresentInVisibility && columnVisibilityConfig[accessorKey] !== false;
  });
  return (
    <div className=" rounded-3 overflow-hidden">
      <BootstrapTable
        bordered
        responsive={responsive}
        striped={striped}
        hover={hover}
        size={size}
        variant={variant}
      >
        <thead>
          <tr>
            {visibleColumns.map((col) => {
              const key = typeof col.accessorKey === 'string'
                ? col.accessorKey
                : String(col.header);
              return (
                <th key={key}>
                  {typeof col.header === 'function'
                    ? col.header(col)
                    : col.header}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="text-gray-800 fw-normal fs-sm">
          {data.map((row, rowIdx) => (
            <tr key={getRowKey(row, rowIdx, rowKey)}>
              {/* Render each cell, using custom renderer if provided */}
              {visibleColumns.map((col) => {
                const key = typeof col.accessorKey === 'string'
                  ? col.accessorKey
                  : String(col.header);
                let cellContent: React.ReactNode = '';
                if (col.cell) {
                  // Custom cell renderer
                  //  for flexibility (e.g. formatting, links or custom components like Badges)
                  cellContent = col.cell(row, col);
                } else if (typeof col.accessorKey === 'string') {
                  if (
                    typeof row === 'object' && row !== null
                    && col.accessorKey in row
                  ) {
                    // Default: just show the value at accessorKey
                    cellContent = (
                      row as Record<string, unknown>
                    )[col.accessorKey] as React.ReactNode;
                  }
                }
                return (
                  <td key={key}>
                    {cellContent}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
        {/* If any column has a footer, render the table footer row */}
        {visibleColumns.some((col) => col.footer) && (
          <tfoot>
            <tr>
              {visibleColumns.map((col) => {
                const key = typeof col.accessorKey === 'string'
                  ? col.accessorKey
                  : String(col.header);
                return (
                  <td key={key}>
                    {typeof col.footer === 'function'
                      ? col.footer(col)
                      : col.footer}
                  </td>
                );
              })}
            </tr>
          </tfoot>
        )}
      </BootstrapTable>
    </div>
  );
}

export function TableSkeleton({
  columns,
  rows = 3,
  className = '',
}: {
  columns: number;
  rows?: number;
  className?: string;
}) {
  return (
    <div className={`rounded-3 overflow-hidden ${className}`}>
      <table className="table table-bordered">
        <thead>
          <tr>
            {Array.from({ length: columns }).map((_, idx) => (
              // It's ok to use array index as key here, since the table is not interactive
              // eslint-disable-next-line react/no-array-index-key
              <th key={`skel-th-${idx}`} className="rounded-2">
                <span className="placeholder col-6 rounded-1" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, rowIdx) => (
            // It's ok to use array index as key here, since the table is not interactive
            // eslint-disable-next-line react/no-array-index-key
            <tr key={`skel-row-${rowIdx}`}>
              {Array.from({ length: columns }).map((__, colIdx) => (
                // It's ok to use array index as key here, since the table is not interactive
                // eslint-disable-next-line react/no-array-index-key
                <td key={`skel-td-${colIdx}`}>
                  <span className="placeholder-glow w-100 d-block">
                    <span
                      className="placeholder rounded-1"
                      // Quite simple and specific styles, so it's ok to use inline styles
                      // It's not necessary to create a new class for this
                      style={{
                        width: `${60 + Math.random() * 30}%`,
                        minHeight: '1.2em',
                        display: 'inline-block',
                      }}
                    />
                  </span>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
